// Renders the Drive WebDAV statistics charts (traffic over time, file type
// distribution, file size distribution) with Chart.js. Mirrors the pattern used by
// email_stats_chart_controller.js, but bundles three charts driven by server-rendered
// (already translated) labels passed in via Stimulus values.

import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler,
} from "chart.js"

Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler
)

const PALETTE = [
  "rgba(59, 130, 246, 0.85)",
  "rgba(16, 185, 129, 0.85)",
  "rgba(245, 158, 11, 0.85)",
  "rgba(239, 68, 68, 0.85)",
  "rgba(139, 92, 246, 0.85)",
  "rgba(236, 72, 153, 0.85)",
  "rgba(20, 184, 166, 0.85)",
  "rgba(107, 114, 128, 0.85)",
  "rgba(6, 182, 212, 0.85)",
]

export default class extends Controller {
  static targets = ["traffic", "extensions", "sizes"]
  static values = {
    trafficLabels: Array,
    trafficUploads: Array,
    trafficDownloads: Array,
    uploadsLabel: String,
    downloadsLabel: String,
    extensionLabels: Array,
    extensionCounts: Array,
    sizeLabels: Array,
    sizeCounts: Array,
    fileCountLabel: String,
  }

  connect() {
    this.charts = []
    this._renderTraffic()
    this._renderExtensions()
    this._renderSizes()
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }

  _renderTraffic() {
    if (!this.hasTrafficTarget) return
    const labels = this.trafficLabelsValue
    if (!labels.length) return

    const step = Math.max(1, Math.ceil(labels.length / 30))
    const displayLabels = labels.map((label, i) => (i % step === 0 ? label : ""))

    this.charts.push(
      new Chart(this.trafficTarget, {
        type: "line",
        data: {
          labels: displayLabels,
          datasets: [
            {
              label: this.uploadsLabelValue,
              data: this.trafficUploadsValue,
              borderColor: PALETTE[0],
              backgroundColor: "rgba(59, 130, 246, 0.10)",
              borderWidth: 2,
              pointRadius: labels.length <= 31 ? 3 : 0,
              tension: 0.3,
              fill: true,
            },
            {
              label: this.downloadsLabelValue,
              data: this.trafficDownloadsValue,
              borderColor: PALETTE[1],
              backgroundColor: "rgba(16, 185, 129, 0.10)",
              borderWidth: 2,
              pointRadius: labels.length <= 31 ? 3 : 0,
              tension: 0.3,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: "index" },
          plugins: {
            legend: { position: "bottom" },
            tooltip: { callbacks: { title: (items) => labels[items[0].dataIndex] || "" } },
          },
          scales: {
            x: { ticks: { maxRotation: 0, autoSkip: true } },
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      })
    )
  }

  _renderExtensions() {
    if (!this.hasExtensionsTarget) return
    const labels = this.extensionLabelsValue
    if (!labels.length) return

    // Right legend eats chart width on phones — put it under the doughnut instead.
    const narrow = typeof window !== "undefined" && window.matchMedia("(max-width: 767.98px)").matches

    this.charts.push(
      new Chart(this.extensionsTarget, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: this.extensionCountsValue,
              backgroundColor: labels.map((_label, i) => PALETTE[i % PALETTE.length]),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: narrow ? "bottom" : "right",
              labels: { boxWidth: 12, padding: narrow ? 8 : 12 },
            },
          },
        },
      })
    )
  }

  _renderSizes() {
    if (!this.hasSizesTarget) return
    const labels = this.sizeLabelsValue
    if (!labels.length) return

    this.charts.push(
      new Chart(this.sizesTarget, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: this.fileCountLabelValue,
              data: this.sizeCountsValue,
              backgroundColor: PALETTE[2],
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      })
    )
  }
}
